import React, { useState } from "react"
import PropTypes from "prop-types"
import Layout from "../../ui/Layout"
import Paper from "../../ui/Paper"
import PaperTitle, { TitleText } from "../../ui/PaperTitle"
import FormGroup from "../../ui/FormGroup"
import Label from "../../ui/Label"
import FormError from "../../ui/FormError"
import ActionsFooter from "../../ui/ActionsFooter"
import Button from "../../ui/Button"
import Input from "../../ui/Input"
import InputRow from "../../ui/InputRow"
import CenteredPage from "../../ui/CenteredPage"
import IconButton from "../../ui/IconButton"
import * as yup from "yup"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useDispatch, useSelector } from "react-redux"
import {
  login,
  logout,
  resetAuth,
  selectAuth,
  selectUpdatingUserAccount,
  updateUserAccountPassword,
} from "./slice"
import { useNavigate } from "react-router-dom"
import ErrorMsg from "../../ui/ErrorMsg"
import LoadingSpinner, { Backdrop } from "../../ui/LoadingSpinner"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import styled from "styled-components"
import astroLogo from "../../icons/icon-astrosite-logo-half-darkback.svg"
import opteraLogo from "../../icons/logo-optera-withtext-ondark.svg"
import Markdown from "react-markdown"

const FormBody = styled.div`
  padding: 2rem 0.5rem 1rem 0.5rem;
  min-width: 17rem;
`

const FormFooter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding-top: 1rem;
`

const InputWithIcon = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
`
const FloatingIcon = styled.div`
  position: absolute;
  right: 0.5rem;
  top: 0.4rem;
`

const AstroLogo = styled.div`
  background-repeat: no-repeat;
  flex: none;
  width: 6rem;
  height: 6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url(${opteraLogo});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  margin: 0 1.5rem 0 0.5rem;
`

const Title = styled(TitleText)`
  font-size: 1.4rem;
  font-weight: bold;
`

const RedPaperTitle = styled(PaperTitle)`
  background-color: rgb(10, 49, 110);
  border-radius: ${(props) => props.theme.corners};
  padding: 1rem 0.5rem;
  font-size: 1.4rem;
  font-weight: bold;
`

const SmallerPaper = styled(Paper)`
  background-color: ${(props) => props.theme.colours.paperSolid};
  width: 25rem;
  padding: 1rem;
  box-shadow: 5px 5px 30px ${(props) => props.theme.colours.paperSolid};
  border: 3px solid #777;
`

const LargerPaper = styled(Paper)`
  background-color: ${(props) => props.theme.colours.paperSolid};
  box-shadow: 5px 5px 30px ${(props) => props.theme.colours.paperSolid};
  border: 3px solid #777;
  max-width: 50rem;
  max-height: 80vh;
  overflow-y: auto;
  padding: 3rem;
  h1 {
    margin-top: 0;
  }
  ${FormFooter} {
    padding-top: 2rem;
  }
`

const AgreementActions = styled.div`
  padding: 1rem 0;
  float: right;
`

const Paragraph = styled.p`
  text-align: center;
  padding: 1rem 1rem 0 1rem;
`

const schema = yup
  .object({
    id: yup.string().required(),
    password: yup.string().required(),
    totp: yup.string(), // Optional, until it's not ;-)
  })
  .required()

const hidden = { display: "none" }

const Login = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const [showPassword, setShowPassword] = useState(false)
  const [userAgreement, setUserAgreement] = useState(null) // string (markdown)
  const { register, handleSubmit, formState, getValues } = useForm({
    resolver: yupResolver(schema),
  })

  // After user has successfully logged in (done here)
  const proceedToDest = () => {
    const dest = localStorage.getItem("previousDestination") || "/"
    navigate(dest)
  }

  // If user declines user agreement
  const doLogout = async () => {
    await dispatch(logout()).unwrap()
    window.location.reload(true)
  }

  const onSubmit = async (data) => {
    try {
      const resp = await dispatch(login(data)).unwrap()
      // If we got a token, we're logged in
      if (resp.token) {
        // If the user has to first agree to an agreement, show that - otherwise
        // navigate immediately.
        if (resp.user_agreement_text) {
          setUserAgreement(resp.user_agreement_text)
        } else {
          proceedToDest()
        }
      }
    } catch (e) {
      console.warn("Login failed", e)
      // UI will show the right thing to do based on global state
    }
  }

  return (
    <Layout>
      <CenteredPage>
        {/* User has to accept an agreement */}
        {userAgreement && (
          <UserAgreement
            userAgreement={userAgreement}
            onAccept={proceedToDest}
            onDecline={doLogout}
          />
        )}
        {/* Non-authenticated User must first reset password */}
        {auth?.password_reset_required && (
          <UnAuthResetPassword
            id={getValues("id")}
            current_password={getValues("password")}
          />
        )}
        {/* User wishes to log in */}
        {!userAgreement && !auth?.password_reset_required && (
          <SmallerPaper>
            <RedPaperTitle>
              <AstroLogo />
              <Title>Astrosite Login</Title>
            </RedPaperTitle>
            {auth?._loadStatus === "loading" && (
              <Backdrop>
                <LoadingSpinner />
              </Backdrop>
            )}
            <FormBody>
              <form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup style={auth?.totp_required ? hidden : undefined}>
                  <Label htmlFor="id">Email</Label>
                  <Input type="text" {...register("id")} autoFocus />
                  <FormError>{formState.errors.id?.message}</FormError>
                </FormGroup>
                <FormGroup style={auth?.totp_required ? hidden : undefined}>
                  <Label htmlFor="password">Password</Label>
                  <InputWithIcon>
                    <Input
                      type={showPassword ? "text" : "password"}
                      {...register("password")}
                    />
                    <FloatingIcon>
                      <FontAwesomeIcon
                        title="Show password"
                        onClick={() => setShowPassword(!showPassword)}
                        icon={showPassword ? faEyeSlash : faEye}
                        color="#ccc"
                      />
                    </FloatingIcon>
                  </InputWithIcon>
                  <FormError>{formState.errors.password?.message}</FormError>
                </FormGroup>
                {/* If a login attempt was rejected because user needs to provide 2FA
                      passcode, ask user for code: */}
                {auth?.totp_required && (
                  <FormGroup>
                    <Paragraph>
                      Two-factor authentication is enabled for this user
                      account. Please provide the authentication code displayed
                      by your authentication device or app:
                    </Paragraph>
                    <Label htmlFor="totp">Authenticator code</Label>
                    <Input type="text" {...register("totp")} autoFocus />
                    <FormError>{formState.errors.id?.message}</FormError>
                  </FormGroup>
                )}
                {auth._loadStatus === "rejected" && !auth?.totp_required && (
                  <FormGroup>
                    <ErrorMsg
                      msg={
                        "That didn't work - please check the provided password"
                      }
                      showIcon={true}
                    />
                  </FormGroup>
                )}
                <FormFooter>
                  <Button
                    type="submit"
                    disabled={auth._loadStatus === "loading"}
                  >
                    {auth._loadStatus === "loading"
                      ? "Please wait..."
                      : "Submit"}
                  </Button>
                </FormFooter>
              </form>
            </FormBody>
          </SmallerPaper>
        )}
      </CenteredPage>
    </Layout>
  )
}

/** Displays a user agreement */
const UserAgreement = ({ userAgreement, onAccept, onDecline }) => {
  return (
    <LargerPaper>
      <Markdown>{userAgreement}</Markdown>
      <AgreementActions>
        <Button onClick={onDecline} delete>
          Decline
        </Button>{" "}
        <Button onClick={onAccept} success autoFocus>
          Accept
        </Button>
      </AgreementActions>
    </LargerPaper>
  )
}

UserAgreement.propTypes = {
  userAgreement: PropTypes.string,
  onAccept: PropTypes.func,
  onDecline: PropTypes.func,
}

const Bullet = styled.li`
  text-align: left;
  font-size: 0.8em;
  padding: 0.5rem 0;
  opacity: 0.8;
`

/** UI to allow the current, unauthenticated user, to reset their password. Takes the "existing" password
 * in as a prop and hides it, as this UI is only shown to a user that has previously tried to log in,
 * and has thus already supplied the UI with their password. */
const UnAuthResetPassword = ({ id, current_password }) => {
  // UI state
  const [showPassword, setShowPassword] = useState(false)

  // App state
  const updatingState = useSelector(selectUpdatingUserAccount)

  // Form state
  const { register, handleSubmit, formState } = useForm({
    resolver: yupResolver(changePasswordSchema),
  })
  const dispatch = useDispatch()
  const toggleShowPassword = (e) => {
    setShowPassword(!showPassword)
    e.preventDefault()
  }

  const onSubmit = async (data) => {
    try {
      await dispatch(
        updateUserAccountPassword({ ...data, id, current_password })
      ).unwrap()

      // If successful, route back to login page by resetting auth state
      //dispatch(resetAuth(id))

      // FIXME because we have separate Redux app state and form state, we can't "partially reset"
      // the Login form to blank out the password easily. No choice but to do a hard reload for now :-(
      window.location.reload(true)
    } catch (e) {
      console.warn("Password reset failed", e)
      // UI will show the right thing to do based on global state
    }
  }

  return (
    <SmallerPaper>
      <RedPaperTitle>
        <Title>Set Password</Title>
      </RedPaperTitle>
      <Paragraph>
        A new password must be set for <strong>{id}</strong>. Please enter a
        secure password that you will use to identify yourself the next time
        that you log in.
      </Paragraph>
      <Paragraph>
        <Bullet>A password length of at least 8 characters</Bullet>
        <Bullet>Inclusion of at least one upper case letter</Bullet>
        <Bullet>Inclusion of at least one lower case letter</Bullet>
        <Bullet>Inclusion of at least one number</Bullet>
        <Bullet>
          Inclusion of at least one special character (e.g. £$%&*@#)
        </Bullet>
        <Bullet>Sufficient complexity</Bullet>
      </Paragraph>
      <FormBody>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Firefox autocomplete-prevention hack. */}
          <input type="text" value={id} style={{ display: "none" }} />
          <input
            type="password"
            value={current_password}
            style={{ display: "none" }}
          />
          <FormGroup>
            <Label>New password:</Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                autoFocus
                {...register("password")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={toggleShowPassword}
                secondary
              />
            </InputRow>
            <FormError>{formState.errors.password?.message}</FormError>
          </FormGroup>
          <FormGroup>
            <Label>Confirm new password:</Label>
            <InputRow>
              <Input
                type={showPassword ? "text" : "password"}
                autoComplete="off"
                {...register("confirmation")}
              />
              <IconButton
                icon={showPassword ? faEyeSlash : faEye}
                onClick={toggleShowPassword}
                secondary
              />
            </InputRow>
            <FormError>{formState.errors.confirmation?.message}</FormError>
          </FormGroup>
          {updatingState?._saveStatus === "updating" && (
            <Backdrop>
              <LoadingSpinner />
            </Backdrop>
          )}
          {updatingState?._saveStatus == "rejected" && (
            <FormGroup>
              <ErrorMsg
                msg={
                  updatingState._errorMessage ||
                  "That didn's work, please try again"
                }
                showIcon={true}
              />
            </FormGroup>
          )}
          <ActionsFooter>
            <Button onClick={() => dispatch(resetAuth())}>Cancel</Button>
            <Button
              type="submit"
              active
              disabled={updatingState?._saveStatus === "updating"}
            >
              Submit
            </Button>
          </ActionsFooter>
        </form>
      </FormBody>
    </SmallerPaper>
  )
}

UnAuthResetPassword.propTypes = {
  // User's current password - as we don't ask them to re-enter. Since this
  // password reset is only shown upon successful login, it's reasonable to
  // assume this password is correct and user does not have to re-enter it.
  current_password: PropTypes.string.isRequired,
  // UserID, usually an email
  id: PropTypes.string.isRequired,
}

/** Form schema for changing password. */
const changePasswordSchema = yup
  .object({
    password: yup.string().required("password is required"),
    confirmation: yup
      .string()
      .oneOf([yup.ref("password"), null], "passwords must match"),
    // FIXME: Plug in custom validation logic as per client requirements
  })
  .required()

export default Login
